// Vücut ölçüleri özeti (2026-10-05) — mezura ölçülerinin son değeri, başlangıca
// göre değişimi ve yorumları (bel riski, bel/kalça, bel/boy). Kurallar:
// utils/bodyMeasurements.js. Ölçümler sekmesinde ve beslenme planında kullanılır.

import React from "react";
import { Box, Tooltip, Typography, alpha } from "@mui/material";
import { COLORS, TYPO } from "../../styles/uiConstants";
import {
  CIRCUMFERENCE_KEYS,
  RISK_LABEL,
  circumferenceProgress,
  waistHeightRatio,
  waistHipRatio,
  waistRisk,
} from "../../utils/bodyMeasurements";

const fmt = (v, d = 1) => Number(v).toLocaleString("tr-TR", { maximumFractionDigits: d });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) : "");

const RISK_TONE = {
  normal: { color: COLORS.successDarker, bg: COLORS.successLight },
  increased: { color: COLORS.warningDarker, bg: COLORS.warningLight },
  high: { color: COLORS.errorDark, bg: COLORS.errorLight },
};

function Indicator({ label, value, risk, hint }) {
  const tone = RISK_TONE[risk] || { color: COLORS.slate700, bg: COLORS.slate100 };
  return (
    <Tooltip title={hint || ""} arrow placement="top">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 140 }}>
        <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{label}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
          {risk ? (
            <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: 1, fontSize: TYPO.caption, color: tone.color, bgcolor: tone.bg }}>
              {RISK_LABEL[risk]}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * @param {Array} records  Measurement kayıtları (hastanın tüm ölçümleri olabilir)
 * @param {object} patient { gender, height }
 * @param {string} [since] Değişimin başlangıcı (ör. plan başlangıç tarihi); yoksa ilk ölçüm
 * @param {number} [targetWaist] Bel hedefi (cm)
 * @param {number} [startWaist] Bel başlangıcı (plan); yoksa ölçümden
 */
export default function BodyMeasurementsSummary({ records, patient, since, targetWaist, startWaist, palette, emptyText }) {
  const prog = circumferenceProgress(records, since);
  const keys = CIRCUMFERENCE_KEYS.filter((k) => prog[k.key]);
  if (!keys.length) {
    return (
      <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
        {emptyText || "Henüz mezura ölçüsü yok. Bel, kalça gibi ölçüler girildiğinde değişim burada görünür."}
      </Typography>
    );
  }
  const waist = prog.waist?.latest;
  const wr = waistRisk(waist, patient?.gender);
  const whr = waistHipRatio(waist, prog.hip?.latest, patient?.gender);
  const wht = waistHeightRatio(waist, patient?.height);
  const tone = palette?.primary || COLORS.slate700;

  // Bel hedefi ilerlemesi
  const sw = Number(startWaist) > 0 ? Number(startWaist) : prog.waist?.baseline;
  const tw = Number(targetWaist);
  const goalPct = waist && sw && tw > 0 && sw !== tw ? Math.max(0, Math.min(1, (sw - waist) / (sw - tw))) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 1.5 }}>
        {keys.map(({ key, label }) => {
          const p = prog[key];
          const down = p.change < 0;
          const changeColor = p.change === 0 || p.count < 2 ? COLORS.slate400 : down ? COLORS.successDark : COLORS.warningDark;
          return (
            <Box key={key} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: alpha(tone, 0.04), border: `1px solid ${COLORS.slate100}` }}>
              <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{label}</Typography>
              <Typography sx={{ fontSize: "1.25rem", color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                {fmt(p.latest)} <Box component="span" sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>cm</Box>
              </Typography>
              <Typography sx={{ fontSize: TYPO.caption, color: changeColor, fontVariantNumeric: "tabular-nums" }}>
                {p.count < 2 ? `${fmtDate(p.latestAt)} · tek ölçüm` : `${p.change > 0 ? "+" : ""}${fmt(p.change)} cm · ${fmtDate(p.baselineAt)}'den beri`}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {waist ? (
          <Indicator
            label="Bel çevresi"
            value={`${fmt(waist)} cm`}
            risk={wr}
            hint={wr ? "DSÖ kesim noktaları: kadında 80 / 88 cm, erkekte 94 / 102 cm" : "Risk için hastanın cinsiyeti gerekli"}
          />
        ) : null}
        {whr ? (
          <Indicator
            label="Bel / kalça oranı"
            value={fmt(whr.ratio, 2)}
            risk={whr.risk}
            hint="Kadında 0,85, erkekte 0,90 ve üstü yüksek risk kabul edilir"
          />
        ) : null}
        {wht ? (
          <Indicator label="Bel / boy oranı" value={fmt(wht.ratio, 2)} risk={wht.risk} hint="0,5 ve üstü riskli, 0,6 ve üstü yüksek" />
        ) : waist ? (
          <Indicator label="Bel / boy oranı" value="—" hint="Hesap için Kişisel Bilgiler'de boy girilmeli" />
        ) : null}
        {goalPct != null ? (
          <Indicator
            label="Bel hedefi"
            value={`${fmt(sw)} → ${fmt(tw)} cm · %${Math.round(goalPct * 100)}`}
            hint={`Güncel ${fmt(waist)} cm; hedefe ${fmt(Math.abs(waist - tw))} cm`}
          />
        ) : null}
      </Box>
      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>
        Eşikler genel tarama amaçlıdır, tanı yerine geçmez.
      </Typography>
    </Box>
  );
}
